import { isEvmChain } from '@/types/vault';
import type { SignatureRequest } from '@/types/vault';

interface Nonce {
  kPublic: string;
  kTwoPublic: string;
}

export interface SignatureSigningParams {
  reservedNonce?: Nonce;
  reservedKeyNonce?: Nonce;
  allSignerKeys?: string[];
  allSignerNonces?: Nonce[];
}

/**
 * Build the M-of-N EVM signing params for a signature request — the reserved
 * nonces for THIS signer plus the canonical all-signer key/nonce arrays the
 * wallet + key need to produce their partial Schnorr contribution. Mirrors the
 * proposal signing-param construction in ProposalDetailPage exactly.
 */
export function buildSignatureSigningParams(
  req: SignatureRequest,
  wkIdentity: string
): SignatureSigningParams {
  let reservedNonce: Nonce | undefined;
  let reservedKeyNonce: Nonce | undefined;
  const sm = req.signingMode;
  if (isEvmChain(req.chain) && req.reservedNonces?.length) {
    const mine = req.reservedNonces.find((rn) => rn.wkIdentity === wkIdentity);
    if (mine) {
      reservedNonce = mine.walletNonce;
      reservedKeyNonce = mine.keyNonce;
    }
  }

  let allSignerKeys: string[] | undefined;
  let allSignerNonces: Nonce[] | undefined;
  if (req.signerPubKeys?.length && req.reservedNonces?.length) {
    const keys: string[] = [];
    const nonces: Nonce[] = [];
    let mismatch = false;
    for (const spk of req.signerPubKeys) {
      if (sm !== 'key_only') keys.push(spk.walletPubKey);
      if (sm !== 'wallet_only') keys.push(spk.keyPubKey);
      const rn = req.reservedNonces.find((n) => n.wkIdentity === spk.wkIdentity);
      if (!rn) {
        mismatch = true;
        break;
      }
      if (sm !== 'key_only') nonces.push(rn.walletNonce);
      if (sm !== 'wallet_only') nonces.push(rn.keyNonce);
    }
    if (!mismatch) {
      allSignerKeys = keys;
      allSignerNonces = nonces;
    }
  }
  return { reservedNonce, reservedKeyNonce, allSignerKeys, allSignerNonces };
}
